import { z } from 'zod';
import { imageUploadField } from '../MessageEditor/MessageImageUpload';
import {
  ActionListDialogAction,
  ActionListDialogSubAction,
  ActionSetting,
  AlertDialogAction,
  FullScreenDialogAction,
  MessageBoxAction,
  OPEN_LINK_OPEN_METHODS,
  OpenLinkAction,
} from './actionSetting';

export type { ActionListDialogAction };

export type ImageResolver = (
  field: z.infer<typeof imageUploadField.schema>,
) => string;

const defaultImageResolver: ImageResolver = imageUploadField.toPreviewUrl;

// ================== Action Form Value Definitions =====================

// ── open_link ────────────────────────────────────────────────────────────────

export const OpenLinkFormValueSchema = z.object({
  type: z.literal('open_link'),
  attribute_schema: z.object({
    url: z.url(),
    open_method: z.enum(OPEN_LINK_OPEN_METHODS),
  }),
});

export const ValidOpenLinkFormValueSchema = z.object({
  type: OpenLinkFormValueSchema.shape.type,
  attribute_schema: z.object({
    url: z.url({ error: '請輸入有效的網址' }),
    open_method: z.enum(OPEN_LINK_OPEN_METHODS),
  }),
});

type OpenLinkFormValue = z.infer<typeof OpenLinkFormValueSchema>;

export const openLinkFormValueInit: OpenLinkFormValue = {
  type: 'open_link',
  attribute_schema: {
    url: 'https://www.youtube.com/watch?v=XqZsoesa55w',
    open_method: 'browser',
  },
};

function openLinkFormValueToAction(
  formValue: OpenLinkFormValue,
): OpenLinkAction {
  return structuredClone(formValue);
}

function openLinkActionToFormValue(
  formValue: OpenLinkAction,
): OpenLinkFormValue {
  return structuredClone(formValue);
}

// ── message_box ──────────────────────────────────────────────────────────────

export const MessageBoxFormValueSchema = z.object({
  type: z.literal('message_box'),
  attribute_schema: z.object({
    title: z.string(),
    message: z.string(),
    positive_button: z.string(),
    image: imageUploadField.schema,
  }),
});

export const ValidMessageBoxFormValueSchema = z.object({
  type: MessageBoxFormValueSchema.shape.type,
  attribute_schema: z.object({
    title: z.string().min(1, { error: '標題不得為空' }),
    message: z.string().min(1, { error: '內文不得為空' }),
    positive_button: z.string().min(1, { error: '按鈕文字不得為空' }),
    image: imageUploadField.validSchema,
  }),
});

export type MessageBoxFormValue = z.infer<typeof MessageBoxFormValueSchema>;
export type ValidMessageBoxFormValue = z.infer<
  typeof ValidMessageBoxFormValueSchema
>;

export const messageBoxFormValueInit: MessageBoxFormValue = {
  type: 'message_box',
  attribute_schema: {
    title: '請輸入標題',
    message: '請輸入內容',
    positive_button: '確認',
    image: imageUploadField.init(null),
  },
};

function messageBoxFormValueToAction(
  formValue: MessageBoxFormValue,
  resolveImage: ImageResolver = defaultImageResolver,
): MessageBoxAction {
  const image = resolveImage(formValue.attribute_schema.image);
  return structuredClone({
    type: 'message_box',
    attribute_schema: { ...formValue.attribute_schema, image },
  });
}

function messageBoxActionToFormValue(
  action: MessageBoxAction,
): MessageBoxFormValue {
  const image = imageUploadField.init(action.attribute_schema.image);
  return structuredClone({
    type: 'message_box',
    attribute_schema: { ...action.attribute_schema, image },
  });
}

// ── alert_dialog ─────────────────────────────────────────────────────────────

export const AlertDialogFormValueSchema = z.object({
  type: z.literal('alert_dialog'),
  attribute_schema: z.object({
    title: z.string(),
    message: z.string(),
    positive_button: z.string(),
    negative_button: z.string(),
    image: imageUploadField.schema,
    get action() {
      return AvailSubActionFormValueSchema;
    },
  }),
});

export const ValidAlertDialogFormValueSchema = z.object({
  type: AlertDialogFormValueSchema.shape.type,
  attribute_schema: z.object({
    title: z.string().min(1, { error: '標題不得為空' }),
    message: z.string().min(1, { error: '內文不得為空' }),
    positive_button: z.string().min(1, { error: '確認按鈕文字不得為空' }),
    negative_button: z.string().min(1, { error: '取消按鈕文字不得為空' }),
    image: imageUploadField.validSchema,
    get action() {
      return ValidAvailSubActionFormValueSchema;
    },
  }),
});

export type AlertDialogFormValue = z.infer<typeof AlertDialogFormValueSchema>;
export type ValidAlertDialogFormValue = z.infer<
  typeof ValidAlertDialogFormValueSchema
>;

export const alertDialogFormValueInit: AlertDialogFormValue = {
  type: 'alert_dialog',
  attribute_schema: {
    title: '請輸入標題',
    message: '請輸入內容',
    positive_button: '確認',
    negative_button: '取消',
    image: imageUploadField.init(null),
    action: openLinkFormValueInit,
  },
};

export function alertDialogFormValueToAction(
  formValue: AlertDialogFormValue,
  resolveImage: ImageResolver = defaultImageResolver,
): AlertDialogAction {
  const image = resolveImage(formValue.attribute_schema.image);
  const action =
    formValue.attribute_schema.action.type === 'message_box'
      ? messageBoxFormValueToAction(
          formValue.attribute_schema.action,
          resolveImage,
        )
      : openLinkFormValueToAction(formValue.attribute_schema.action);

  return structuredClone({
    type: 'alert_dialog',
    attribute_schema: { ...formValue.attribute_schema, image, action },
  });
}

export function alertDialogActionToFormValue(
  actionSetting: AlertDialogAction,
): AlertDialogFormValue {
  const image = imageUploadField.init(
    actionSetting.attribute_schema.image ?? null,
  );

  const formValueSubAction = (() => {
    const actionSettingSubAction = actionSetting.attribute_schema.action;

    const isValidSubAction = isAvailActionSettingSubAction(
      actionSettingSubAction,
    );

    if (!isValidSubAction) {
      return alertDialogFormValueInit['attribute_schema']['action'];
    }

    const formValueSubAction =
      actionSettingSubAction.type === 'message_box'
        ? messageBoxActionToFormValue(actionSettingSubAction)
        : openLinkActionToFormValue(actionSettingSubAction);

    return formValueSubAction;
  })();

  return structuredClone({
    type: 'alert_dialog',
    attribute_schema: {
      ...actionSetting.attribute_schema,
      image,
      action: formValueSubAction,
    },
  });
}

// ── full_screen_dialog ────────────────────────────────────────────────────────

export const FullScreenDialogFormValueSchema = z.object({
  type: z.literal('full_screen_dialog'),
  attribute_schema: z.object({
    image: imageUploadField.schema,
    get action() {
      return AvailSubActionFormValueSchema;
    },
  }),
});

// NOTE: full_screen_dialog 沒有純字串欄位，僅驗證 image 與嵌套 action
export const ValidFullScreenDialogFormValueSchema = z.object({
  type: FullScreenDialogFormValueSchema.shape.type,
  attribute_schema: z.object({
    image: imageUploadField.validSchema,
    get action() {
      return ValidAvailSubActionFormValueSchema;
    },
  }),
});

export type FullScreenDialogFormValue = z.infer<
  typeof FullScreenDialogFormValueSchema
>;
export type ValidFullScreenDialogFormValue = z.infer<
  typeof ValidFullScreenDialogFormValueSchema
>;

export const fullScreenDialogFormValueInit: FullScreenDialogFormValue = {
  type: 'full_screen_dialog',
  attribute_schema: {
    image: imageUploadField.init(null),
    action: openLinkFormValueInit,
  },
};

export function fullScreenDialogFormValueToAction(
  formValue: FullScreenDialogFormValue,
  resolveImage: ImageResolver = defaultImageResolver,
): FullScreenDialogAction {
  const image = resolveImage(formValue.attribute_schema.image);
  const action =
    formValue.attribute_schema.action.type === 'message_box'
      ? messageBoxFormValueToAction(
          formValue.attribute_schema.action,
          resolveImage,
        )
      : openLinkFormValueToAction(formValue.attribute_schema.action);

  return structuredClone({
    type: 'full_screen_dialog',
    attribute_schema: { image, action },
  });
}

export function fullScreenActionToFormValue(
  actionSetting: FullScreenDialogAction,
): FullScreenDialogFormValue {
  const image = imageUploadField.init(
    actionSetting.attribute_schema.image ?? null,
  );

  const formValueSubAction = (() => {
    const actionSettingSubAction = actionSetting.attribute_schema.action;

    const isValidSubAction = isAvailActionSettingSubAction(
      actionSettingSubAction,
    );

    if (!isValidSubAction) {
      return alertDialogFormValueInit['attribute_schema']['action'];
    }

    const formValueSubAction =
      actionSettingSubAction.type === 'message_box'
        ? messageBoxActionToFormValue(actionSettingSubAction)
        : openLinkActionToFormValue(actionSettingSubAction);

    return formValueSubAction;
  })();

  return structuredClone({
    type: 'full_screen_dialog',
    attribute_schema: {
      ...actionSetting.attribute_schema,
      image,
      action: formValueSubAction,
    },
  });
}
// ── action_list_dialog ────────────────────────────────────────────────────────

export const ActionListDialogFormValueSchema = z.object({
  type: z.literal('action_list_dialog'),
  attribute_schema: z.object({
    title: z.string(),
    message: z.string(),
    get actions() {
      return z
        .object({
          button_name: z.string(),
          action: AvailSubActionFormValueSchema,
        })
        .array()
        .min(1, {
          error: '請新增按鈕',
        });
    },
    image: imageUploadField.schema,
  }),
});

export const ValidActionListDialogFormValueSchema = z.object({
  type: ActionListDialogFormValueSchema.shape.type,
  attribute_schema: z.object({
    title: z.string().min(1, { error: '標題不得為空' }),
    message: z.string().min(1, { error: '內文不得為空' }),
    get actions() {
      return z
        .object({
          button_name: z.string().min(1, { error: '按鈕名稱不得為空' }),
          action: ValidAvailSubActionFormValueSchema,
        })
        .array()
        .min(1, {
          error: '請新增按鈕',
        });
    },
    image: imageUploadField.validSchema,
  }),
});

export type ActionListDialogFormValue = z.infer<
  typeof ActionListDialogFormValueSchema
>;
export type ValidActionListDialogFormValue = z.infer<
  typeof ValidActionListDialogFormValueSchema
>;

export const actionListDialogFormValueInit: ActionListDialogFormValue = {
  type: 'action_list_dialog',
  attribute_schema: {
    title: '請輸入標題',
    message: '請輸入內容',
    image: {
      kind: 'empty',
    },
    actions: [
      {
        button_name: '按鈕名稱',
        action: {
          type: 'open_link',
          attribute_schema: {
            url: 'https://placehold.co/600x400?text=Hello\nWorld',
            open_method: 'in_app',
          },
        },
      },
    ],
  },
};

export const actionListDialogActionFormValueInit =
  actionListDialogFormValueInit.attribute_schema.actions[0];

export function actionListDialogFormValueToAction(
  formValue: ActionListDialogFormValue,
  resolveImage: ImageResolver = defaultImageResolver,
): ActionListDialogAction {
  const transedActions: ActionListDialogSubAction[] =
    formValue.attribute_schema.actions.map(({ button_name, action }) => {
      if (action.type === 'message_box') {
        return {
          button_name,
          action: messageBoxFormValueToAction(action, resolveImage),
        };
      }

      return {
        button_name,
        action: openLinkFormValueToAction(action),
      };
    });
  const image = resolveImage(formValue.attribute_schema.image);

  const actionSetting: ActionListDialogAction = {
    type: 'action_list_dialog',
    attribute_schema: {
      ...formValue.attribute_schema,
      image,
      actions: transedActions,
    },
  };
  return structuredClone(actionSetting);
}

type ActionListDialogActionSettingButton =
  ActionListDialogAction['attribute_schema']['actions'][number];

type ActionListDialogActionSettingAvailButton = {
  button_name: string;
  action: AvailDialogActionAvailAction;
};

function isAvailActionSettingButton(
  value: ActionListDialogActionSettingButton,
): value is ActionListDialogActionSettingAvailButton {
  return isAvailActionSettingSubAction(value.action);
}

export function actionListDialogActionToFormValue(
  actionSetting: ActionListDialogAction,
): ActionListDialogFormValue {
  const image = imageUploadField.init(
    actionSetting.attribute_schema.image ?? null,
  );

  const formValueButtons = (() => {
    const buttons = actionSetting.attribute_schema.actions;
    const validButtons = buttons.filter(isAvailActionSettingButton);

    if (validButtons.length < 1) {
      return actionListDialogFormValueInit['attribute_schema']['actions'];
    }

    const transedFormValueButtons = validButtons.map(
      ({ button_name, action: subAction }) => {
        const formValueSubAction =
          subAction.type === 'message_box'
            ? messageBoxActionToFormValue(subAction)
            : openLinkActionToFormValue(subAction);

        return {
          button_name,
          action: formValueSubAction,
        };
      },
    );

    return transedFormValueButtons;
  })();

  return {
    type: 'action_list_dialog',
    attribute_schema: {
      ...actionSetting.attribute_schema,
      image,
      actions: formValueButtons,
    },
  };
}

// ── Union schemas (ActionFormValue / AvailSubAction) ──────────────────────────

export const ActionSettingFormValueSchema = z.discriminatedUnion('type', [
  ActionListDialogFormValueSchema,
  AlertDialogFormValueSchema,
  FullScreenDialogFormValueSchema,
  MessageBoxFormValueSchema,
  OpenLinkFormValueSchema,
]);

export const ValidActionSettingFormValueSchema = z.discriminatedUnion('type', [
  ValidActionListDialogFormValueSchema,
  ValidAlertDialogFormValueSchema,
  ValidFullScreenDialogFormValueSchema,
  ValidMessageBoxFormValueSchema,
  ValidOpenLinkFormValueSchema,
]);

export type ActionSettingFormValue = z.infer<
  typeof ActionSettingFormValueSchema
>;
export type ValidActionSettingFormValue = z.infer<
  typeof ValidActionSettingFormValueSchema
>;

/**
 * Here is the only Avail sub action used in action,
 * Config the available Sub action setting here
 */
const AvailSubActionFormValueSchema = z.discriminatedUnion('type', [
  MessageBoxFormValueSchema,
  OpenLinkFormValueSchema,
]);

export const ValidAvailSubActionFormValueSchema = z.discriminatedUnion('type', [
  ValidMessageBoxFormValueSchema,
  ValidOpenLinkFormValueSchema,
]);

export type AvailSubActionFormValue = z.infer<
  typeof AvailSubActionFormValueSchema
>;

export type AvailSubActionType = AvailSubActionFormValue['type'];

export const AVAIL_SUB_ACTION_TYPES = [
  'open_link',
  'message_box',
] satisfies AvailSubActionType[];

type AvailDialogActionAvailAction = OpenLinkAction | MessageBoxAction;

function isAvailActionSettingSubAction(
  value: ActionSetting,
): value is AvailDialogActionAvailAction {
  return AVAIL_SUB_ACTION_TYPES.includes(value.type as AvailSubActionType);
}

// ===================== The action / forms data transformer =====================

export function toFormValue(
  actionSetting: ActionSetting,
): ActionSettingFormValue | undefined {
  if (actionSetting.type === 'action_list_dialog') {
    return actionListDialogActionToFormValue(actionSetting);
  }

  if (actionSetting.type === 'alert_dialog') {
    return alertDialogActionToFormValue(actionSetting);
  }
  if (actionSetting.type === 'full_screen_dialog') {
    return fullScreenActionToFormValue(actionSetting);
  }
  if (actionSetting.type === 'message_box') {
    return messageBoxActionToFormValue(actionSetting);
  }
  return openLinkActionToFormValue(actionSetting);
}

export function toActionSetting(
  formValue: ActionSettingFormValue,
  resolveImage: ImageResolver = defaultImageResolver,
): ActionSetting {
  if (formValue.type === 'action_list_dialog') {
    return actionListDialogFormValueToAction(formValue, resolveImage);
  }

  if (formValue.type === 'alert_dialog') {
    return alertDialogFormValueToAction(formValue, resolveImage);
  }
  if (formValue.type === 'full_screen_dialog') {
    return fullScreenDialogFormValueToAction(formValue, resolveImage);
  }
  if (formValue.type === 'message_box') {
    return messageBoxFormValueToAction(formValue, resolveImage);
  }

  return openLinkFormValueToAction(formValue);
}
